import { useEffect, useMemo, useState } from "react";
import {
  getAdminStats,
  getContactEnquiries,
  getDesignerEnquiries,
  getMeetingBookings,
  getFurnitureEnquiries,
  getQuotationToolQuotations,
  adminMediaUrl,
  getFurnitureEnquiry,
  updateFurnitureEnquiryStatus,
  downloadFurnitureFloorplan,
  clearAdminSession,
  isAdminAuthenticated,
} from "../services/adminApi";
import "./AdminDashboard.css";

const TABS = ["Overview", "Contact Enquiries", "Designer Enquiries", "Meetings", "Furniture Enquiries", "Quotations"];
const PAGE_SIZE = 10;

function formatDate(value) {
  if (!value) return "-";

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value);

  return new Intl.DateTimeFormat("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  }).format(date);
}

function formatNeeds(value) {
  if (!value) return "-";

  if (Array.isArray(value)) {
    return value.length ? value.join(", ") : "-";
  }

  if (typeof value === "string") {
    try {
      const parsed = JSON.parse(value);
      if (Array.isArray(parsed)) {
        return parsed.length ? parsed.join(", ") : "-";
      }
    } catch (error) {
      return value;
    }
  }

  return String(value);
}

function getField(row, keys) {
  const key = keys.find((candidate) => row?.[candidate] !== undefined && row?.[candidate] !== null);
  return key ? row[key] : "";
}

function escapeCell(value) {
  return String(value ?? "-")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");
}

function downloadExcelFile({ filename, sheetName, headers, rows }) {
  const tableRows = rows
    .map(
      (row) =>
        `<tr>${row.map((cell) => `<td>${escapeCell(cell || "-")}</td>`).join("")}</tr>`
    )
    .join("");

  const worksheet = `
    <html xmlns:o="urn:schemas-microsoft-com:office:office"
      xmlns:x="urn:schemas-microsoft-com:office:excel"
      xmlns="http://www.w3.org/TR/REC-html40">
      <head>
        <meta charset="UTF-8" />
        <!--[if gte mso 9]>
          <xml>
            <x:ExcelWorkbook>
              <x:ExcelWorksheets>
                <x:ExcelWorksheet>
                  <x:Name>${escapeCell(sheetName)}</x:Name>
                  <x:WorksheetOptions><x:DisplayGridlines /></x:WorksheetOptions>
                </x:ExcelWorksheet>
              </x:ExcelWorksheets>
            </x:ExcelWorkbook>
          </xml>
        <![endif]-->
      </head>
      <body>
        <table>
          <thead>
            <tr>${headers.map((header) => `<th>${escapeCell(header)}</th>`).join("")}</tr>
          </thead>
          <tbody>${tableRows}</tbody>
        </table>
      </body>
    </html>`;

  const blob = new Blob([worksheet], { type: "application/vnd.ms-excel;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");

  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(url);
}

function toContactExportRow(row) {
  return [
    row.name || "-",
    row.phone || "-",
    row.email || "-",
    getField(row, ["project_type", "projectType"]) || "-",
    row.location || "-",
    row.area || "-",
    getField(row, ["project_stage", "projectStage", "stage"]) || "-",
    formatNeeds(row.needs),
    row.budget || "-",
    formatDate(row.created_at || row.createdAt),
  ];
}

function toDesignerExportRow(row) {
  return [
    row.name || "-",
    row.phone || "-",
    row.city || "-",
    formatDate(row.created_at || row.createdAt),
  ];
}

function formatMeetingDate(value) {
  if (!value) return "-";
  if (typeof value === "string" && /^\d{4}-\d{2}-\d{2}/.test(value)) {
    return value.slice(0, 10);
  }
  return formatDate(value);
}

function formatMeetingTime(value) {
  if (!value) return "-";
  const text = String(value);
  const [hourText, minute = "00"] = text.split(":");
  let hour = Number(hourText);
  if (Number.isNaN(hour)) return text;
  const period = hour >= 12 ? "PM" : "AM";
  hour = hour % 12 || 12;
  return `${String(hour).padStart(2, "0")}:${minute} ${period}`;
}

function getReminderStatus(row) {
  const reminders = [
    ["24h", row.reminder_24h_sent_at],
    ["12h", row.reminder_12h_sent_at],
    ["6h", row.reminder_6h_sent_at],
    ["3h", row.reminder_3h_sent_at],
    ["1h", row.reminder_1h_sent_at],
    ["30m", row.reminder_30m_sent_at],
    ["15m", row.reminder_15m_sent_at],
  ];
  const sent = reminders.filter(([, value]) => value).map(([label]) => label);
  return sent.length ? sent.join(", ") : "Pending";
}

function toMeetingExportRow(row) {
  return [
    row.id || "-",
    row.name || "-",
    row.phone || "-",
    row.email || "-",
    row.city || "-",
    formatMeetingDate(row.meeting_date),
    formatMeetingTime(row.meeting_time),
    row.status || "-",
    getReminderStatus(row),
    formatDate(row.created_at || row.createdAt),
  ];
}

function toQuotationExportRow(row) {
  return [
    row.quotation_no || "-",
    row.client_name || "-",
    row.phone || "-",
    row.email || "-",
    row.property_type || "-",
    row.carpet_area || "-",
    row.package_name || "-",
    row.base_amount || 0,
    row.added_amount || 0,
    row.removed_amount || 0,
    row.total_amount || 0,
    row.owner_email_status || "-",
    row.client_email_status || "-",
    formatDate(row.created_at),
  ];
}

function StateBlock({ type, message }) {
  return (
    <div className={`admin-state admin-state--${type}`}>
      <span>{message}</span>
    </div>
  );
}

function StatCard({ label, value }) {
  return (
    <section className="admin-stat-card">
      <span>{label}</span>
      <strong>{Number(value || 0).toLocaleString("en-IN")}</strong>
    </section>
  );
}

function SearchBox({ value, onChange, placeholder }) {
  return (
    <label className="admin-search">
      <span>Search</span>
      <input
        type="search"
        value={value}
        onChange={(event) => onChange(event.target.value)}
        placeholder={placeholder}
      />
    </label>
  );
}

function DownloadButton({ disabled, onClick }) {
  return (
    <button className="admin-download" disabled={disabled} type="button" onClick={onClick}>
      Download Excel
    </button>
  );
}

function Pagination({ currentPage, onPageChange, totalItems }) {
  const totalPages = Math.ceil(totalItems / PAGE_SIZE);
  const startItem = (currentPage - 1) * PAGE_SIZE + 1;
  const endItem = Math.min(currentPage * PAGE_SIZE, totalItems);

  if (totalPages <= 1) {
    return (
      <div className="admin-pagination admin-pagination--single">
        Showing {totalItems.toLocaleString("en-IN")} entries
      </div>
    );
  }

  return (
    <div className="admin-pagination" aria-label="Table pagination">
      <span>
        Showing {startItem.toLocaleString("en-IN")}-{endItem.toLocaleString("en-IN")} of{" "}
        {totalItems.toLocaleString("en-IN")}
      </span>
      <div className="admin-pagination-actions">
        <button
          disabled={currentPage === 1}
          onClick={() => onPageChange(currentPage - 1)}
          type="button"
        >
          Previous
        </button>
        <strong>
          {currentPage} / {totalPages}
        </strong>
        <button
          disabled={currentPage === totalPages}
          onClick={() => onPageChange(currentPage + 1)}
          type="button"
        >
          Next
        </button>
      </div>
    </div>
  );
}

function ContactTable({ rows, currentPage, onPageChange, totalItems }) {
  return (
    <>
      <div className="admin-table-wrap">
        <table className="admin-table">
          <thead>
            <tr>
              <th>Name</th>
              <th>Phone</th>
              <th>Email</th>
              <th>Project Type</th>
              <th>Location</th>
              <th>Area</th>
              <th>Project Stage</th>
              <th>Needs</th>
              <th>Budget</th>
              <th>Created Date</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row, index) => (
              <tr key={row.id || row.submission_id || index}>
                <td>{row.name || "-"}</td>
                <td>{row.phone || "-"}</td>
                <td>{row.email || "-"}</td>
                <td>{getField(row, ["project_type", "projectType"]) || "-"}</td>
                <td>{row.location || "-"}</td>
                <td>{row.area || "-"}</td>
                <td>{getField(row, ["project_stage", "projectStage", "stage"]) || "-"}</td>
                <td>{formatNeeds(row.needs)}</td>
                <td>{row.budget || "-"}</td>
                <td>{formatDate(row.created_at || row.createdAt)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <Pagination currentPage={currentPage} onPageChange={onPageChange} totalItems={totalItems} />
    </>
  );
}

function DesignerTable({ rows, currentPage, onPageChange, totalItems }) {
  return (
    <>
      <div className="admin-table-wrap">
        <table className="admin-table admin-table--compact">
          <thead>
            <tr>
              <th>Name</th>
              <th>Phone</th>
              <th>City</th>
              <th>Created Date</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row, index) => (
              <tr key={row.id || row.submission_id || index}>
                <td>{row.name || "-"}</td>
                <td>{row.phone || "-"}</td>
                <td>{row.city || "-"}</td>
                <td>{formatDate(row.created_at || row.createdAt)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <Pagination currentPage={currentPage} onPageChange={onPageChange} totalItems={totalItems} />
    </>
  );
}

function MeetingsTable({ rows, currentPage, onPageChange, totalItems }) {
  return (
    <>
      <div className="admin-table-wrap">
        <table className="admin-table">
          <thead>
            <tr>
              <th>Booking ID</th>
              <th>Name</th>
              <th>Phone</th>
              <th>Email</th>
              <th>City</th>
              <th>Date</th>
              <th>Time</th>
              <th>Status</th>
              <th>Reminder Status</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row, index) => (
              <tr key={row.id || index}>
                <td>{row.id || "-"}</td>
                <td>{row.name || "-"}</td>
                <td>{row.phone || "-"}</td>
                <td>{row.email || "-"}</td>
                <td>{row.city || "-"}</td>
                <td>{formatMeetingDate(row.meeting_date)}</td>
                <td>{formatMeetingTime(row.meeting_time)}</td>
                <td>{row.status || "-"}</td>
                <td>{getReminderStatus(row)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <Pagination currentPage={currentPage} onPageChange={onPageChange} totalItems={totalItems} />
    </>
  );
}

function FurnitureEnquiryPanel() {
  const [rows, setRows] = useState([]);
  const [query, setQuery] = useState("");
  const [selected, setSelected] = useState(null);
  const [detail, setDetail] = useState(null);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(true);

  useEffect(() => {
    getFurnitureEnquiries()
      .then(setRows)
      .catch((requestError) => setError(requestError.message))
      .finally(() => setBusy(false));
  }, []);

  const filtered = useMemo(() => {
    const value = query.trim().toLowerCase();
    if (!value) return rows;
    return rows.filter((row) => [row.id, row.name, row.phone, row.email, row.property_type, row.status].join(" ").toLowerCase().includes(value));
  }, [query, rows]);

  async function openDetails(row) {
    setSelected(row);
    setDetail(null);
    try { setDetail(await getFurnitureEnquiry(row.id)); }
    catch (requestError) { setError(requestError.message); }
  }

  async function changeStatus(row, status) {
    try {
      await updateFurnitureEnquiryStatus(row.id, status);
      setRows((current) => current.map((item) => item.id === row.id ? { ...item, status } : item));
      setSelected((current) => current?.id === row.id ? { ...current, status } : current);
      setDetail((current) => current?.id === row.id ? { ...current, status } : current);
    } catch (requestError) { setError(requestError.message); }
  }

  function parseRooms(value) {
    if (Array.isArray(value)) return value;
    try { return JSON.parse(value || "[]"); } catch (error) { return []; }
  }

  return (
    <section className="admin-panel">
      <div className="admin-panel-header">
        <h2>Furniture Enquiries</h2>
        <SearchBox value={query} onChange={setQuery} placeholder="Search furniture enquiries" />
      </div>
      {busy && <StateBlock type="loading" message="Loading furniture enquiries..." />}
      {error && <StateBlock type="error" message={error} />}
      {!busy && !error && !filtered.length && <StateBlock type="empty" message="No furniture enquiries found." />}
      {!busy && !error && filtered.length > 0 && (
        <div className="admin-table-wrap">
          <table className="admin-table admin-table--compact">
            <thead><tr><th>Name</th><th>Phone</th><th>Email</th><th>Property</th><th>Estimate</th><th>Status</th><th>Received</th><th>Details</th></tr></thead>
            <tbody>{filtered.map((row) => (
              <tr key={row.id}>
                <td>{row.name}</td><td>{row.phone}</td><td>{row.email || "-"}</td><td>{row.property_type || "-"}</td>
                <td>₹{Number(row.grand_total || 0).toLocaleString("en-IN")}</td>
                <td><select className="admin-status-select" value={row.status || "new"} onChange={(event) => changeStatus(row, event.target.value)}>
                  <option value="new">New</option><option value="contacted">Contacted</option><option value="in_progress">In progress</option><option value="closed">Closed</option>
                </select></td>
                <td>{formatDate(row.created_at)}</td>
                <td><button type="button" className="admin-inline-action" onClick={() => openDetails(row)}>View</button></td>
              </tr>
            ))}</tbody>
          </table>
        </div>
      )}
      {selected && (
        <div className="admin-detail-backdrop" role="presentation" onMouseDown={(event) => { if (event.target === event.currentTarget) setSelected(null); }}>
          <section className="admin-detail-dialog" role="dialog" aria-modal="true" aria-label="Furniture enquiry details">
            <div className="admin-detail-heading"><div><p>Furniture enquiry #{selected.id}</p><h2>{selected.name}</h2></div><button type="button" aria-label="Close details" onClick={() => setSelected(null)}>×</button></div>
            {!detail ? <StateBlock type="loading" message="Loading enquiry details..." /> : <>
              <div className="admin-detail-contact"><span>{detail.phone}</span><span>{detail.email || "Email not provided"}</span><span>{detail.property_type || "Property not provided"}</span></div>
              <div className="admin-room-list">{parseRooms(detail.rooms_json).map((room, index) => (
                <section key={`${room.room}-${index}`}><header><strong>{room.room || `Room ${index + 1}`}</strong><span>{room.size || "-"} ft · ₹{Number(room.total || 0).toLocaleString("en-IN")}</span></header>
                  {(room.items || []).map((item, itemIndex) => <div key={itemIndex}><span>{item.l || item.name}</span><span>₹{Number(item.a ?? item.price ?? 0).toLocaleString("en-IN")}</span></div>)}
                </section>
              ))}</div>
              <dl className="admin-estimate-totals"><div><dt>Subtotal</dt><dd>₹{Number(detail.subtotal || 0).toLocaleString("en-IN")}</dd></div><div><dt>Installation</dt><dd>₹{Number(detail.installation || 0).toLocaleString("en-IN")}</dd></div><div><dt>GST</dt><dd>₹{Number(detail.gst || 0).toLocaleString("en-IN")}</dd></div><div><dt>Grand total</dt><dd>₹{Number(detail.grand_total || 0).toLocaleString("en-IN")}</dd></div></dl>
              {detail.notes && <p className="admin-detail-notes"><strong>Notes:</strong> {detail.notes}</p>}
              {detail.floorplan_name && <button className="admin-download" type="button" onClick={() => downloadFurnitureFloorplan(detail.id).catch((requestError) => setError(requestError.message))}>Download floor plan · {detail.floorplan_name}</button>}
              <label className="admin-detail-status">Status<select className="admin-status-select" value={detail.status || "new"} onChange={(event) => changeStatus(detail, event.target.value)}><option value="new">New</option><option value="contacted">Contacted</option><option value="in_progress">In progress</option><option value="closed">Closed</option></select></label>
            </>}
          </section>
        </div>
      )}
    </section>
  );
}

function AdminDashboard() {
  const [activeTab, setActiveTab] = useState("Overview");
  const [stats, setStats] = useState(null);
  const [contactRows, setContactRows] = useState([]);
  const [designerRows, setDesignerRows] = useState([]);
  const [meetingRows, setMeetingRows] = useState([]);
  const [quotationRows, setQuotationRows] = useState([]);
  const [loading, setLoading] = useState({ stats: true, contact: false, designer: false, meetings: false, quotations: false });
  const [errors, setErrors] = useState({ stats: "", contact: "", designer: "", meetings: "", quotations: "" });
  const [contactSearch, setContactSearch] = useState("");
  const [designerSearch, setDesignerSearch] = useState("");
  const [meetingSearch, setMeetingSearch] = useState("");
  const [quotationSearch, setQuotationSearch] = useState("");
  const [contactPage, setContactPage] = useState(1);
  const [designerPage, setDesignerPage] = useState(1);
  const [meetingPage, setMeetingPage] = useState(1);
  const [quotationPage, setQuotationPage] = useState(1);

  useEffect(() => {
    if (!isAdminAuthenticated()) {
      window.location.replace("/admin/login");
    }
  }, []);

  useEffect(() => {
    let isMounted = true;

    if (!isAdminAuthenticated()) {
      setLoading((current) => ({ ...current, stats: false }));
      return () => {
        isMounted = false;
      };
    }

    getAdminStats()
      .then((data) => {
        if (isMounted) setStats(data);
      })
      .catch((error) => {
        if (isMounted) setErrors((current) => ({ ...current, stats: error.message }));
      })
      .finally(() => {
        if (isMounted) setLoading((current) => ({ ...current, stats: false }));
      });

    return () => {
      isMounted = false;
    };
  }, []);

  useEffect(() => {
    if (activeTab !== "Contact Enquiries" || contactRows.length || loading.contact) return;

    setLoading((current) => ({ ...current, contact: true }));
    setErrors((current) => ({ ...current, contact: "" }));
    getContactEnquiries()
      .then(setContactRows)
      .catch((error) => setErrors((current) => ({ ...current, contact: error.message })))
      .finally(() => setLoading((current) => ({ ...current, contact: false })));
  }, [activeTab, contactRows.length, loading.contact]);

  useEffect(() => {
    if (activeTab !== "Designer Enquiries" || designerRows.length || loading.designer) return;

    setLoading((current) => ({ ...current, designer: true }));
    setErrors((current) => ({ ...current, designer: "" }));
    getDesignerEnquiries()
      .then(setDesignerRows)
      .catch((error) => setErrors((current) => ({ ...current, designer: error.message })))
      .finally(() => setLoading((current) => ({ ...current, designer: false })));
  }, [activeTab, designerRows.length, loading.designer]);

  useEffect(() => {
    if (activeTab !== "Meetings" || meetingRows.length || loading.meetings) return;

    setLoading((current) => ({ ...current, meetings: true }));
    setErrors((current) => ({ ...current, meetings: "" }));
    getMeetingBookings()
      .then(setMeetingRows)
      .catch((error) => setErrors((current) => ({ ...current, meetings: error.message })))
      .finally(() => setLoading((current) => ({ ...current, meetings: false })));
  }, [activeTab, loading.meetings, meetingRows.length]);

  useEffect(() => {
    if (activeTab !== "Quotations" || quotationRows.length || loading.quotations) return;
    setLoading((current) => ({ ...current, quotations: true }));
    setErrors((current) => ({ ...current, quotations: "" }));
    getQuotationToolQuotations()
      .then(setQuotationRows)
      .catch((error) => setErrors((current) => ({ ...current, quotations: error.message })))
      .finally(() => setLoading((current) => ({ ...current, quotations: false })));
  }, [activeTab, loading.quotations, quotationRows.length]);

  const filteredContactRows = useMemo(() => {
    const query = contactSearch.trim().toLowerCase();
    if (!query) return contactRows;

    return contactRows.filter((row) =>
      [
        row.name,
        row.phone,
        row.email,
        row.project_type,
        row.location,
        row.area,
        row.project_stage,
        formatNeeds(row.needs),
        row.budget,
      ]
        .join(" ")
        .toLowerCase()
        .includes(query)
    );
  }, [contactRows, contactSearch]);

  const filteredDesignerRows = useMemo(() => {
    const query = designerSearch.trim().toLowerCase();
    if (!query) return designerRows;

    return designerRows.filter((row) =>
      [row.name, row.phone, row.city].join(" ").toLowerCase().includes(query)
    );
  }, [designerRows, designerSearch]);

  const filteredMeetingRows = useMemo(() => {
    const query = meetingSearch.trim().toLowerCase();
    if (!query) return meetingRows;

    return meetingRows.filter((row) =>
      [
        row.id,
        row.name,
        row.phone,
        row.email,
        row.city,
        row.meeting_date,
        formatMeetingTime(row.meeting_time),
        row.status,
        getReminderStatus(row),
      ]
        .join(" ")
        .toLowerCase()
        .includes(query)
    );
  }, [meetingRows, meetingSearch]);

  const filteredQuotationRows = useMemo(() => {
    const query = quotationSearch.trim().toLowerCase();
    if (!query) return quotationRows;
    return quotationRows.filter((row) =>
      [row.quotation_no, row.client_name, row.phone, row.email, row.property_type, row.package_name]
        .join(" ").toLowerCase().includes(query)
    );
  }, [quotationRows, quotationSearch]);

  useEffect(() => {
    setContactPage(1);
  }, [contactSearch]);

  useEffect(() => {
    setDesignerPage(1);
  }, [designerSearch]);

  useEffect(() => {
    setMeetingPage(1);
  }, [meetingSearch]);

  useEffect(() => {
    setQuotationPage(1);
  }, [quotationSearch]);

  const paginatedContactRows = useMemo(() => {
    const startIndex = (contactPage - 1) * PAGE_SIZE;
    return filteredContactRows.slice(startIndex, startIndex + PAGE_SIZE);
  }, [contactPage, filteredContactRows]);

  const paginatedDesignerRows = useMemo(() => {
    const startIndex = (designerPage - 1) * PAGE_SIZE;
    return filteredDesignerRows.slice(startIndex, startIndex + PAGE_SIZE);
  }, [designerPage, filteredDesignerRows]);

  const paginatedMeetingRows = useMemo(() => {
    const startIndex = (meetingPage - 1) * PAGE_SIZE;
    return filteredMeetingRows.slice(startIndex, startIndex + PAGE_SIZE);
  }, [meetingPage, filteredMeetingRows]);

  const paginatedQuotationRows = useMemo(() => {
    const startIndex = (quotationPage - 1) * PAGE_SIZE;
    return filteredQuotationRows.slice(startIndex, startIndex + PAGE_SIZE);
  }, [quotationPage, filteredQuotationRows]);

  function handleLogout() {
    clearAdminSession();
    window.location.replace("/admin/login");
  }

  function handleContactDownload() {
    downloadExcelFile({
      filename: "dwellora-contact-enquiries.xls",
      sheetName: "Contact Enquiries",
      headers: [
        "Name",
        "Phone",
        "Email",
        "Project Type",
        "Location",
        "Area",
        "Project Stage",
        "Needs",
        "Budget",
        "Created Date",
      ],
      rows: filteredContactRows.map(toContactExportRow),
    });
  }

  function handleDesignerDownload() {
    downloadExcelFile({
      filename: "dwellora-designer-enquiries.xls",
      sheetName: "Designer Enquiries",
      headers: ["Name", "Phone", "City", "Created Date"],
      rows: filteredDesignerRows.map(toDesignerExportRow),
    });
  }

  function handleMeetingDownload() {
    downloadExcelFile({
      filename: "dwellora-meeting-bookings.xls",
      sheetName: "Meetings",
      headers: [
        "Booking ID",
        "Name",
        "Phone",
        "Email",
        "City",
        "Date",
        "Time",
        "Status",
        "Reminder Status",
        "Created Date",
      ],
      rows: filteredMeetingRows.map(toMeetingExportRow),
    });
  }

  function handleQuotationDownload() {
    downloadExcelFile({
      filename: "dwellora-quotation-records.xls",
      sheetName: "Quotation Records",
      headers: ["Quotation No.", "Client", "Phone", "Email", "Property", "Carpet Area", "Package", "Base Amount", "Added", "Removed", "Total", "Owner Email", "Client Email", "Created Date"],
      rows: filteredQuotationRows.map(toQuotationExportRow),
    });
  }

  return (
    <main className="admin-dashboard">
      <header className="admin-topbar">
        <div>
          <p>Dwellora Designs</p>
          <h1>Admin Dashboard</h1>
        </div>
        <div className="admin-topbar-actions">
          <a className="admin-topbar-link" href="/admin/catalogue">Manage catalogue</a>
          <a className="admin-topbar-link" href="/admin/quote-pricing">Quotation tool pricing</a>
          <button className="admin-logout" type="button" onClick={handleLogout}>
            Logout
          </button>
        </div>
      </header>

      <nav className="admin-tabs" aria-label="Admin dashboard tabs">
        {TABS.map((tab) => (
          <button
            key={tab}
            type="button"
            className={activeTab === tab ? "is-active" : ""}
            onClick={() => setActiveTab(tab)}
          >
            {tab}
          </button>
        ))}
      </nav>

      {activeTab === "Overview" && (
        <section className="admin-panel">
          {loading.stats && <StateBlock type="loading" message="Loading overview..." />}
          {errors.stats && <StateBlock type="error" message={errors.stats} />}
          {!loading.stats && !errors.stats && (
            <div className="admin-stats-grid">
              <StatCard label="Total Enquiries" value={stats?.totalEnquiries} />
              <StatCard label="Contact Enquiries" value={stats?.contactEnquiries} />
              <StatCard label="Designer Enquiries" value={stats?.designerEnquiries} />
              <StatCard label="Meeting Bookings" value={stats?.meetingBookings} />
              <StatCard label="Furniture Enquiries" value={stats?.furnitureEnquiries} />
              <StatCard label="Total Products" value={stats?.totalProducts} />
              <StatCard label="Active Products" value={stats?.activeProducts} />
              <StatCard label="Total Quotations" value={stats?.totalQuotations} />
              <StatCard label="Draft Quotations" value={stats?.draftQuotations} />
            </div>
          )}
        </section>
      )}

      {activeTab === "Contact Enquiries" && (
        <section className="admin-panel">
          <div className="admin-panel-header">
            <h2>Contact Enquiries</h2>
            <div className="admin-panel-actions">
              <DownloadButton
                disabled={loading.contact || errors.contact || filteredContactRows.length === 0}
                onClick={handleContactDownload}
              />
              <SearchBox
                value={contactSearch}
                onChange={setContactSearch}
                placeholder="Search contact enquiries"
              />
            </div>
          </div>
          {loading.contact && <StateBlock type="loading" message="Loading contact enquiries..." />}
          {errors.contact && <StateBlock type="error" message={errors.contact} />}
          {!loading.contact && !errors.contact && filteredContactRows.length === 0 && (
            <StateBlock type="empty" message="No contact enquiries found." />
          )}
          {!loading.contact && !errors.contact && filteredContactRows.length > 0 && (
            <ContactTable
              currentPage={contactPage}
              onPageChange={setContactPage}
              rows={paginatedContactRows}
              totalItems={filteredContactRows.length}
            />
          )}
        </section>
      )}

      {activeTab === "Designer Enquiries" && (
        <section className="admin-panel">
          <div className="admin-panel-header">
            <h2>Designer Enquiries</h2>
            <div className="admin-panel-actions">
              <DownloadButton
                disabled={loading.designer || errors.designer || filteredDesignerRows.length === 0}
                onClick={handleDesignerDownload}
              />
              <SearchBox
                value={designerSearch}
                onChange={setDesignerSearch}
                placeholder="Search designer enquiries"
              />
            </div>
          </div>
          {loading.designer && <StateBlock type="loading" message="Loading designer enquiries..." />}
          {errors.designer && <StateBlock type="error" message={errors.designer} />}
          {!loading.designer && !errors.designer && filteredDesignerRows.length === 0 && (
            <StateBlock type="empty" message="No designer enquiries found." />
          )}
          {!loading.designer && !errors.designer && filteredDesignerRows.length > 0 && (
            <DesignerTable
              currentPage={designerPage}
              onPageChange={setDesignerPage}
              rows={paginatedDesignerRows}
              totalItems={filteredDesignerRows.length}
            />
          )}
        </section>
      )}

      {activeTab === "Meetings" && (
        <section className="admin-panel">
          <div className="admin-panel-header">
            <h2>Meetings</h2>
            <div className="admin-panel-actions">
              <DownloadButton
                disabled={loading.meetings || errors.meetings || filteredMeetingRows.length === 0}
                onClick={handleMeetingDownload}
              />
              <SearchBox
                value={meetingSearch}
                onChange={setMeetingSearch}
                placeholder="Search meetings"
              />
            </div>
          </div>
          {loading.meetings && <StateBlock type="loading" message="Loading meetings..." />}
          {errors.meetings && <StateBlock type="error" message={errors.meetings} />}
          {!loading.meetings && !errors.meetings && filteredMeetingRows.length === 0 && (
            <StateBlock type="empty" message="No meetings found." />
          )}
          {!loading.meetings && !errors.meetings && filteredMeetingRows.length > 0 && (
            <MeetingsTable
              currentPage={meetingPage}
              onPageChange={setMeetingPage}
              rows={paginatedMeetingRows}
              totalItems={filteredMeetingRows.length}
            />
          )}
        </section>
      )}
      {activeTab === "Quotations" && (
        <section className="admin-panel">
          <div className="admin-panel-header">
            <h2>Quotation Records</h2>
            <div className="admin-panel-actions">
              <DownloadButton disabled={loading.quotations || errors.quotations || filteredQuotationRows.length === 0} onClick={handleQuotationDownload} />
              <SearchBox value={quotationSearch} onChange={setQuotationSearch} placeholder="Search quotation, client or phone" />
            </div>
          </div>
          {loading.quotations && <StateBlock type="loading" message="Loading quotation records..." />}
          {errors.quotations && <StateBlock type="error" message={errors.quotations} />}
          {!loading.quotations && !errors.quotations && filteredQuotationRows.length === 0 && <StateBlock type="empty" message="No quotation records found." />}
          {!loading.quotations && !errors.quotations && filteredQuotationRows.length > 0 && (
            <>
              <div className="admin-table-wrap">
                <table className="admin-table">
                  <thead><tr><th>Quotation</th><th>Client</th><th>Phone</th><th>Email</th><th>Property</th><th>Package</th><th>Total</th><th>PDF</th><th>Created</th></tr></thead>
                  <tbody>{paginatedQuotationRows.map((row) => (
                    <tr key={row.id}>
                      <td>{row.quotation_no || "-"}</td><td>{row.client_name || "-"}</td><td>{row.phone || "-"}</td><td>{row.email || "-"}</td>
                      <td>{row.property_type || "-"}</td><td>{row.package_name || "-"}</td><td>₹{Number(row.total_amount || 0).toLocaleString("en-IN")}</td>
                      <td>{row.pdf_path ? <a className="admin-inline-action" href={adminMediaUrl(row.pdf_path)} target="_blank" rel="noreferrer">Open PDF</a> : "-"}</td><td>{formatDate(row.created_at)}</td>
                    </tr>
                  ))}</tbody>
                </table>
              </div>
              <Pagination currentPage={quotationPage} onPageChange={setQuotationPage} totalItems={filteredQuotationRows.length} />
            </>
          )}
        </section>
      )}
      {activeTab === "Furniture Enquiries" && <FurnitureEnquiryPanel />}
    </main>
  );
}

export default AdminDashboard;
